import { useContext, useEffect, useState } from 'react';
import { IPlan } from '../../../../api/plans/interfaces/planInterface';
import { ApplicationContext } from '../../../../contexts/ApplicationContext';
import { formatCurrency } from '../../../../utils/formatCurrency';
import { toAmount } from '../../../../utils/toAmount';
import { planCashDiscount } from '../../../../utils/planCashDiscount';
import { PlanContext } from '../Context/PlanContext';
import { useInstallmentTax } from '../hooks/useInstallmentTax';

type PlanButtonProps = { plan: IPlan };

import './styles.scss';
import { getColorBrightness } from '../../../../utils/get-color-brightness';
import { useIsDarkMode } from '../../../../hooks/useIsDarkMode';
import { readableTextColor } from '../../../../utils/readableTextColor';
import { CheckoutPageContext } from '../Context/CheckoutPageContext';
import { principalColor } from '../PageBlocks/appearance';

export const PlanButton = ({ plan }: PlanButtonProps) => {
	const [hasTaxes, setHasTaxes] = useState<boolean>(true);

	const { finance } = useContext(ApplicationContext);
	const { plan: currentPlan, setPlan, plans } = useContext(PlanContext);
	const { customization } = useContext(ApplicationContext);
	// Tema personalizado: plano selecionado e "Recomendado" na COR PRINCIPAL
	// (antes era sempre a cor da plataforma, fora da paleta escolhida).
	const pageSettings = useContext(CheckoutPageContext);
	const principal = principalColor(pageSettings);
	// Tema personalizado: plano NÃO selecionado com a cor de "Bordas e
	// divisórias" (antes cinza fixo, fora da paleta).
	const isCustomTheme = pageSettings?.theme === 'custom';
	const highlight = principal || customization?.primaryColor;
	const isDark = useIsDarkMode();

	const cycleLabel = {
		1: 'por semana',
		2: 'por quinzena',
		3: 'por mês',
		4: 'por bimestre',
		5: 'por trimestre',
		6: 'por semestre',
		7: 'por ano',
		8: 'a cada 2 anos'
	};

	const alternativeCycleLabel = {
		1: 'semana',
		2: 'quinzena',
		3: 'mês',
		4: 'bimestre',
		5: 'trimestre',
		6: 'semestre',
		7: 'ano',
		8: '2 anos'
	};

	const getAlternativeCycleLabel = (cycleId: number) => {
		const cycle = cycleId as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
		return alternativeCycleLabel[cycle];
	};

	useEffect(() => {
		const pricingSchema = plan?.pricingSchema;

		const hasTaxes =
			(finance?.isAnticipationEnabled ?? true) &&
			pricingSchema?.anticipationResponsible === 'CUSTOMER';

		setHasTaxes(hasTaxes);
	}, [plan]);

	const getCycleLabel = (cycleId: number) => {
		const cycle = cycleId as 1 | 2 | 3 | 4 | 5 | 6 | 7;
		return cycleLabel[cycle];
	};

	const handleChangeCurrentPlan = (id: number) => {
		const plan = plans?.find((plan) => plan.id === id);

		if (!plan) return;

		setPlan(plan);
	};

	const { calculate, installments } = useInstallmentTax();

	useEffect(() => {
		calculate(
			plan.price,
			plan?.pricingSchema.maxInstallment || 1,
			hasTaxes
		);
	}, [plan, hasTaxes]);

	// Desconto à vista com a regra do pay (só de bimestral a anual). Antes subtraía em qualquer
	// ciclo e dizia só "à vista", sem o valor do desconto.
	const cashDiscount = planCashDiscount({
		...plan,
		price: toAmount(plan.price)
	});
	const priceWithDiscount = toAmount(plan.price) - cashDiscount;

	function getBorderColor() {
		if (principal) return principal;
		if (!customization?.primaryColor) {
			return '';
		}

		const isPrimaryColorDark =
			getColorBrightness(customization.primaryColor) === 'dark';

		if (isDark && isPrimaryColorDark) {
			return '#fff';
		}

		return customization.primaryColor;
	}

	const customLabels: Record<string, string> = {
		RXZvbHV0aW9uIEJsYWNrIEZyaWRheV9YX3VuZGVmaW5lZF8xNzYxMjQ2MDI2MjM3:
			'Oferta Exclusiva de R$397 por R$97 o PRIMEIRO MÊS',
		'RXZvbHV0aW9uIEFQUCBTZWxmY2hlY2tvdXQgQmxhY2sgRnJpZGF5X1hfdW5kZWZpbmVkXzE3NjE5MTM4MjQ2OTE=':
			'Oferta Exclusiva de R$997 por R$97 os PRIMEIROS DOIS MESES'
	};
	const customLabelKey = Object.keys(customLabels).find((l) =>
		location.pathname.includes(l)
	);

	return (
		<button
			type="button"
			key={plan.id}
			onClick={() => handleChangeCurrentPlan(plan.id)}
			className={`${
				currentPlan?.id === plan.id ? 'active' : ''
			} bg-transparent rounded rounded-lg py-5 px-4 my-4 btn-block text-left ribbon ribbon-right plan-button`}
			style={{
				border:
					currentPlan?.id === plan.id
						? `3px solid ${getBorderColor()}`
						: isCustomTheme
						? '2px solid var(--checkout-border, #E9ECEF)'
						: isDark
						? '2px solid #343A40'
						: '2px solid #E9ECEF'
			}}
		>
			{plan.recommended && (
				<div
					className="ribbon-target rounded float-right position-relative"
					style={{
						top: '0px',
						right: '-18px',
						backgroundColor: highlight,
						// O fundo é a cor da plataforma: o texto precisa
						// acompanhar. `text-dark` fixo dava 2,95 de contraste
						// sobre um roxo, nos dois temas.
						color: readableTextColor(highlight)
					}}
				>
					Recomendado
				</div>
			)}
			<h5 className="h5 text-gray-800">{plan.name}</h5>

			<h4 className="h3 m-0 mt-4 text-gray-700 d-inline-block">
				{customLabelKey ? (
					<>{customLabels[customLabelKey]}</>
				) : (
					<>
						{(plan?.pricingSchema.maxInstallment || 1) > 1
							? formatCurrency(
									installments[installments.length - 1]
										?.price || 0
							  )
							: formatCurrency(plan.price)}

						{(plan?.pricingSchema.maxInstallment || 1) > 1 && (
							<span className="d-inline-block ml-2 font-size-lg text-gray-600">
								em {plan?.pricingSchema.maxInstallment || 1}x{' '}
								{!finance?.isAnticipationEnabled && 'sem juros'}
							</span>
						)}

						{(plan?.pricingSchema.maxInstallment || 1) === 1 && (
							<span className="d-inline-block font-size-lg text-gray-800">
								/{getAlternativeCycleLabel(plan.cycle)}
							</span>
						)}
					</>
				)}
			</h4>
			{(plan?.pricingSchema.maxInstallment || 1) > 1 ? (
				<div className="font-weight-bold text-gray-600">
					<span className="d-block text-gray-600 mt-1">
						ou {formatCurrency(priceWithDiscount)} à vista /{' '}
						{getCycleLabel(plan.cycle)}
						{cashDiscount > 0 && (
							<>
								{' '}
								· {formatCurrency(cashDiscount)} de desconto em
								1x
							</>
						)}
					</span>
				</div>
			) : (
				<></>
			)}
			{plan.trialDays ? (
				<span
					className="d-block mt-2 font-weight-bold"
					style={{ color: customization?.primaryColor }}
				>
					Teste gratuitamente por {plan.trialDays} dias
				</span>
			) : null}

			{plan.description && currentPlan?.id === plan.id ? (
				<p
					className="text-gray-600 mb-0 mt-4"
					dangerouslySetInnerHTML={{ __html: plan.description }}
				></p>
			) : (
				<></>
			)}
		</button>
	);
};
